import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Map, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showBackButton = location.pathname !== '/dashboard';

  const menuItems = [
    { name: 'Suggestions', icon: '📍', path: '/suggestions', action: () => navigate('/suggestions') },
    { name: 'Statistics', icon: '📊', path: '/statistics', action: () => navigate('/statistics') },
    { name: 'Settings', icon: '⚙️', path: '/settings', action: () => navigate('/settings') },
    { name: 'Log Out', icon: '🚪', path: null, action: handleLogout }
  ];

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left side - back button */}
            {showBackButton ? (
              <button
                onClick={handleBack}
                className="p-2 rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm hover:bg-white hover:shadow-md transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
            ) : (
              <div className="w-10"></div>
            )}
            
            {/* Centered logo and title */}
            <div className="flex items-center">
              <Map className="h-8 w-8 text-indigo-600" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">
                MapMarker
              </h1>
            </div>
            
            {/* Right side - hamburger menu */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm hover:bg-white hover:shadow-md transition-all duration-200"
            >
              {menuOpen ? (
                <X className="w-5 h-5 text-gray-700" />
              ) : (
                <Menu className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hamburger Menu */}
      {menuOpen && (
        <div className="absolute top-16 right-4 z-20 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px]">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {user?.first_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email
                  }
                </p>
                <p className="text-xs text-gray-500">MapMarker</p>
              </div>
            </div>
          </div>
          
          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setMenuOpen(false);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors ${
                  item.path && location.pathname === item.path 
                    ? 'bg-indigo-50 border-r-2 border-indigo-600' 
                    : ''
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className={`text-gray-700 ${item.path && location.pathname === item.path ? 'font-medium text-indigo-700' : ''}`}>
                  {item.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
